import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DocsSidebar from "./DocsSidebar";
import DocsContent from "./DocsContent";
import MarketingInfoBar from "../MarketingInfoBar";
import PageMeta from "../PageMeta";
import { parseSummary } from "./parseSummary";

const SIDEBAR_WIDTH = 280;
const NAV_OFFSET = 88;

const sleekScrollbar = {
  scrollbarWidth: "thin",
  scrollbarColor: "color-mix(in oklab, var(--ink-5) 45%, transparent) transparent",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "color-mix(in oklab, var(--ink-5) 40%, transparent)",
    borderRadius: "var(--r-pill)",
    border: "2px solid transparent",
    backgroundClip: "content-box",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "color-mix(in oklab, var(--ink-5) 75%, transparent)",
    backgroundClip: "content-box",
  },
};

export default function DocsLayout() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [items, setItems] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL || ""}/docs/SUMMARY.md`)
      .then((r) => (r.ok ? r.text() : ""))
      .then((t) => {
        if (!cancelled) setItems(parseSummary(t));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sidebar = (
    <DocsSidebar items={items} onNavigate={() => setMobileOpen(false)} />
  );

  return (
    <>
      <PageMeta
        title="Docs — Control Plane for Agent Driven Infrastructure"
        description="InfraBlaze documentation — product guides, workbench reference, asset inventory, BlazeAI, cost intelligence, integrations, and deployment."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "InfraBlaze",
              "item": "https://www.infrablaze.com/",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Docs",
              "item": "https://www.infrablaze.com/docs",
            },
          ],
        }}
      />
      <Box
        component="main"
        sx={{
          flex: 1,
          pt: "calc(56px + env(safe-area-inset-top) + 16px)",
          pb: { xs: 6, md: 8 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative" }}
        >
          {!isMdUp ? (
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  border: "1px solid var(--ink-3)",
                  borderRadius: 1.5,
                }}
                aria-label="Open docs navigation"
              >
                <MenuIcon />
              </IconButton>
              <Box
                component="span"
                sx={{ fontWeight: 800, letterSpacing: 0.5, fontSize: "0.92rem" }}
              >
                Docs
              </Box>
            </Box>
          ) : null}

          <Box sx={{ display: "flex", gap: { md: 4, lg: 5 }, alignItems: "flex-start" }}>
            {isMdUp ? (
              <Box
                sx={{
                  flex: `0 0 ${SIDEBAR_WIDTH}px`,
                  width: SIDEBAR_WIDTH,
                  alignSelf: "flex-start",
                  position: "sticky",
                  top: `${NAV_OFFSET}px`,
                  maxHeight: `calc(100vh - ${NAV_OFFSET}px - 16px)`,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  borderRight: "1px solid var(--ink-3)",
                  pr: 2,
                  ...sleekScrollbar,
                }}
              >
                {sidebar}
              </Box>
            ) : (
              <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                PaperProps={{
                  sx: {
                    width: SIDEBAR_WIDTH,
                    pt: "calc(56px + env(safe-area-inset-top) + 8px)",
                    px: 1.5,
                    ...sleekScrollbar,
                  },
                }}
              >
                {sidebar}
              </Drawer>
            )}

            <Box
              component="article"
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "860px",
                mx: { md: "auto" },
                py: { xs: 1, md: 2 },
              }}
            >
              <DocsContent />
            </Box>
          </Box>
        </Container>
      </Box>
      <MarketingInfoBar />
    </>
  );
}
